import { useCallback } from "react";
import { Message } from "@/services/typings";
import { getAgentList } from "@/services";
import { generateUuid, getAgentFromScript } from "@/utils";
import toast from "react-hot-toast";

interface UseAgentInitListParams {
  setAgentData: (data: any) => void;
  setDialogueMessages: (messages: Message[]) => void;
  setLocale: (locale: any) => void;
  applyBrandColor?: (color: string) => void;
  svgLoadingSetter?: (loading: boolean) => void;
  setOpenModal?: (open: boolean) => void;
  setIsLauncherVisible?: (visible: boolean) => void;
}

/**
 * 统一获取智能体初始化数据的 hook
 * 避免在多个组件中重复定义相同的逻辑
 */
export const useAgentInitList = ({
  setAgentData,
  setDialogueMessages,
  setLocale,
  applyBrandColor,
  svgLoadingSetter,
  setOpenModal,
  setIsLauncherVisible,
}: UseAgentInitListParams) => {
  /**
   * 处理消息数据，添加必要的字段
   */
  const processMessages = (messages: Message[] | undefined): Message[] => {
    return (
      messages?.map((item: Message) => ({
        ...item,
        id: item?.id || generateUuid(),
        isShowAction: item?.find_answer ? true : false,
      })) || []
    );
  };

  /**
   * 获取智能体初始化列表
   * 统一处理数据逻辑，避免重复请求和状态不一致
   */
  const getAgentInitList = useCallback(async () => {
    try {
      // 如果有 svgLoadingSetter，设置加载状态
      if (svgLoadingSetter) {
        svgLoadingSetter(true);
      }

      const res: any = await getAgentList({
        agent: getAgentFromScript(),
      });

      if (res.code === 0) {
        const processedMessages = processMessages(res?.data?.messages);

        const resultData = {
          ...res?.data,
          messages: processedMessages,
        };

        // 设置智能体数据
        setAgentData(resultData);

        // 设置本地语言
        setLocale(resultData?.setting?.lang);

        // 设置动态品牌色
        if (applyBrandColor) {
          applyBrandColor(resultData?.setting?.brand_color);
        }

        // 设置对话框信息数据
        setDialogueMessages(processedMessages);
      } else {
        console.error("获取智能体数据失败:", res.err_msg);
        toast.error(res.err_msg);
      }
    } catch (error) {
      console.error("获取智能体数据异常:", error);
    } finally {
      // 在 finally 中关闭加载状态并显示弹窗
      if (svgLoadingSetter) {
        svgLoadingSetter(false);
      }
      if (setOpenModal) {
        setOpenModal(true);
      }
      if (setIsLauncherVisible) {
        setIsLauncherVisible(false);
      }
    }
  }, [
    setAgentData,
    setDialogueMessages,
    setLocale,
    applyBrandColor,
    svgLoadingSetter,
    setOpenModal,
    setIsLauncherVisible,
  ]);

  return { getAgentInitList };
};
